'use client';

import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Modal, Fade, useTheme, useMediaQuery, Divider, Skeleton } from '@mui/material';
import { Venue } from '../../../../shared/models';
import AvailabilitiesContent from './availabilities_content';
import SimilarVenues from '../../components/similar_venues';
import ContactBox from '../../components/contact_box';
import { useAvailabilityStore } from '../../stores/availability_store';
import LinkWithLoading from '../../components/link_with_loading';
import { trackEvent } from '../../lib/analytics';
import SmallLogo from '../../components/small_logo';

interface AvailabilitiesBoxProps {
    venue: Venue;
    showFutureAvailabilityAlert: boolean;
}
export const AvailabilitiesBox: React.FC<AvailabilitiesBoxProps> = ({ venue, showFutureAvailabilityAlert }) => {
    const { initializeVenue, venue: storeVenue, selectedSection, setSelectedDate, setNumberOfPeople } = useAvailabilityStore();
    const [modalOpen, setModalOpen] = useState(false);
    const [modalOpenTime, setModalOpenTime] = useState<Date | null>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [searchParams] = useState(() => {
        if (typeof window === 'undefined') return new URLSearchParams();
        return new URLSearchParams(window.location.search);
    });

    useEffect(() => {
        if (!venue) return;

        initializeVenue(venue);

        const dateParam = searchParams.get('date');
        if (dateParam) {
            setSelectedDate(new Date(dateParam));
        }

        const sizeParam = searchParams.get('size');
        if (sizeParam) {
            const size = parseInt(sizeParam);
            if (!isNaN(size)) {
                setNumberOfPeople(size);
            }
        }

        return () => {
            initializeVenue(null);
        };
    }, [venue?.ID]);

    // Remove the storeVenue dependency for rendering
    if (!venue) {
        return <Skeleton variant="rectangular" height={500} />;
    }

    const shouldShowSimilarVenues = !venue.Enabled && venue.ViewableForInquiry && venue.SimilarVenueIds && venue.SimilarVenueIds.length > 0;

    // Handle opening the availability modal on mobile
    const handleOpenModal = () => {
        setModalOpen(true);
        setModalOpenTime(new Date());
        trackEvent('availability_modal_opened', {
            venue_id: venue?.ID,
            venue_name: venue?.Name,
            device_type: 'mobile',
            timestamp: new Date().toISOString()
        });
    };

    // Handle closing the availability modal on mobile
    const handleCloseModal = () => {
        if (modalOpenTime) {
            const timeSpentMs = new Date().getTime() - modalOpenTime.getTime();
            trackEvent('availability_modal_abandoned', {
                venue_id: venue?.ID,
                venue_name: venue?.Name,
                device_type: 'mobile',
                time_spent_seconds: Math.round(timeSpentMs / 1000),
                timestamp: new Date().toISOString()
            });
        }
        setModalOpenTime(null);
        setModalOpen(false);
    };

    if (isMobile) {
        return (
            <>
                {!modalOpen && (
                    <Box
                        sx={{
                            position: 'relative',
                        }}
                    >
                        <Box
                            sx={{
                                position: 'fixed',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: '70px',
                                backgroundColor: '#1C1C34',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 3000,
                                boxShadow: '0px -4px 15px rgba(0, 0, 0, 0.2)',
                                padding: '0 16px',
                            }}
                        >
                            <Button
                                onClick={handleOpenModal}
                                sx={{
                                    backgroundColor: '#F0D1FF',
                                    color: '#1C1C34',
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: '1.1rem',
                                    fontWeight: 'bold',
                                    padding: '12px 24px',
                                    borderRadius: '12px',
                                    width: '100%',
                                    zIndex: 3000,
                                }}
                            >
                                Check Availability
                            </Button>
                        </Box>
                    </Box>
                )}
                <Modal
                    open={modalOpen}
                    onClose={handleCloseModal}
                    closeAfterTransition
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Fade in={modalOpen}>
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                bgcolor: '#1C1C34',
                                color: '#F0D1FF',
                                boxShadow: 24,
                                p: 1,
                                overflowY: 'auto',
                                position: 'relative',
                            }}
                        >
                            <div className="pt-4">
                                <SmallLogo />
                            </div>

                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 64,
                                    left: 16,
                                    backgroundColor: 'transparent',
                                    color: '#F0D1FF',
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: '1.3rem',
                                }}
                            >
                                {venue?.Name}
                            </Box>
                            <Button
                                onClick={handleCloseModal}
                                sx={{
                                    position: 'absolute',
                                    top: 16,
                                    right: 16,
                                    backgroundColor: '#F0D1FF',
                                    color: '#1C1C34',
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: '#E0B1EF',
                                    },
                                }}
                            >
                                Close
                            </Button>
                            <Box mt={9}>
                                {shouldShowSimilarVenues ? (
                                    <>
                                        <Divider sx={{
                                            borderColor: '#F0D1FF',
                                            my: 2,
                                        }} />
                                        <Typography
                                            variant="h4"
                                            sx={{
                                                color: '#F0D1FF',
                                                fontFamily: 'Inter',
                                                fontSize: { xs: '1.25rem', md: '1.5rem' },
                                                fontWeight: '300',
                                                lineHeight: '1.4',
                                                letterSpacing: '-0.02em',
                                                maxWidth: '600px',
                                                mx: 'auto',
                                                pt: 2,
                                                '& strong': {
                                                    color: '#fff',
                                                    fontWeight: '400'
                                                }
                                            }}
                                        >
                                            <strong>Contact required for booking.</strong> The host needs to confirm availability before you can reserve. Browse similar venues below that offer instant booking.
                                        </Typography>
                                        <ContactBox
                                            name={venue?.HostFirstNameLastInitial ? `${venue.HostFirstNameLastInitial}` : 'Litty Team'}
                                            email={'support@getlitty.com'}
                                            venue={venue}
                                        />
                                        <SimilarVenues
                                            currentVenue={venue}
                                            venueBookedTimeSlots={{}}
                                            attributeIcons={{}} />
                                    </>
                                ) : (
                                    <AvailabilitiesContent venue={venue} />
                                )}
                            </Box>
                        </Box>
                    </Fade>
                </Modal >
            </>
        );
    }

    return (
        venue && (
            <Box sx={{ marginRight: 2 }}>
                {shouldShowSimilarVenues ? (
                    <SimilarVenues
                        currentVenue={venue}
                        venueBookedTimeSlots={{}}
                        attributeIcons={{}}
                    />
                ) : (
                    <AvailabilitiesContent venue={venue} />
                )}
            </Box>
        )
    );
};